import React, { useState, useRef, useEffect } from 'react';
import { aiApi, ticketsApi, formatApiError } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Bot, 
  Send, 
  User, 
  Loader2,
  Sparkles,
  Ticket,
  RefreshCw,
  Volume2,
  VolumeX
} from 'lucide-react';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

export default function AIChatPage() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Ciao! Sono REBECCA, la tua assistente virtuale. Come posso aiutarti oggi? Descrivimi il tuo problema e cercherò di darti una diagnosi e possibili soluzioni!'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState('');
  const [tickets, setTickets] = useState([]);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchTickets = async () => {
    try {
      const response = await ticketsApi.getAll({ page_size: 20 });
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Fetch tickets error:', error);
    }
  };

  const playVoice = async (text) => {
    if (!voiceEnabled) return;
    setSpeaking(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ai/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text: text.slice(0, 500) })
      });
      const data = await res.json();
      if (data.audio) {
        const audio = new Audio(data.audio);
        audioRef.current = audio;
        audio.onended = () => setSpeaking(false);
        audio.play();
      }
    } catch (e) {
      console.error('TTS error:', e);
    } finally {
      setTimeout(() => setSpeaking(false), 10000);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setSpeaking(false);
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const ticketContext = selectedTicket 
        ? tickets.find(t => t.id === selectedTicket)?.description 
        : '';
      
      const response = await aiApi.chat(userMessage, selectedTicket || null, ticketContext);
      
      const aiText = response.data.response;
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: aiText,
        suggestions: {
          category: response.data.suggested_category,
          priority: response.data.suggested_priority
        }
      }]);
      
      // Play REBECCA's voice
      playVoice(aiText);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Mi dispiace, si è verificato un errore. Per favore riprova.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    stopSpeaking();
    setMessages([
      {
        role: 'assistant',
        content: 'Chat cancellata. Come posso aiutarti?'
      }
    ]);
  };

  return (
    <div className="h-[calc(100vh-180px)] flex flex-col" data-testid="ai-chat-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <div className="w-10 h-10 rounded-md bg-[#FDE047] flex items-center justify-center ai-pulse">
              <Bot className="w-6 h-6 text-[#0F172A]" />
            </div>
            REBECCA
          </h1>
          <p className="text-[#71717A] mt-1">Ciao! Sono REBECCA, la tua assistente. Descrivimi il problema!</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedTicket || 'none'} onValueChange={(value) => setSelectedTicket(value === 'none' ? '' : value)}>
            <SelectTrigger className="w-[200px]" data-testid="select-ticket">
              <Ticket className="w-4 h-4 mr-2 text-[#71717A]" />
              <SelectValue placeholder="Seleziona ticket" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nessun ticket</SelectItem>
              {tickets.map(ticket => (
                <SelectItem key={ticket.id} value={ticket.id}>
                  #{ticket.id.slice(-6)} - {ticket.title.substring(0, 20)}...
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={clearChat} data-testid="clear-chat-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button 
            variant={voiceEnabled ? 'default' : 'outline'} 
            onClick={() => { setVoiceEnabled(!voiceEnabled); if (speaking) stopSpeaking(); }}
            className={voiceEnabled ? 'bg-[#16A34A] hover:bg-[#16A34A]/90 text-white' : 'border-[#E4E4E7]'}
            data-testid="voice-toggle-btn"
          >
            {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Chat Area */}
      <Card className="flex-1 border border-[#E4E4E7] shadow-none flex flex-col overflow-hidden">
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((message, idx) => (
              <div 
                key={idx} 
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`chat-message-${idx}`}
              >
                <div className={`flex gap-3 max-w-[80%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.role === 'user' 
                      ? 'bg-[#002FA7] text-white' 
                      : 'bg-[#FDE047] text-[#0F172A]'
                  }`}>
                    {message.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>
                  <div>
                    <div className={`p-4 rounded-lg ${
                      message.role === 'user' 
                        ? 'bg-[#002FA7] text-white' 
                        : 'bg-[#F4F4F5] text-[#0F172A] border border-[#E4E4E7]'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                    {message.suggestions && (message.suggestions.category || message.suggestions.priority) && (
                      <div className="flex gap-2 mt-2">
                        {message.suggestions.category && (
                          <span className="text-xs bg-blue-100 text-[#002FA7] px-2 py-1 rounded">
                            Categoria suggerita: {message.suggestions.category}
                          </span>
                        )}
                        {message.suggestions.priority && (
                          <span className="text-xs bg-amber-100 text-[#D97706] px-2 py-1 rounded">
                            Priorità suggerita: {message.suggestions.priority}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#FDE047] flex items-center justify-center">
                    <Bot className="w-4 h-4 text-[#0F172A]" />
                  </div>
                  <div className="p-4 bg-[#F4F4F5] rounded-lg border border-[#E4E4E7]">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#002FA7]" />
                      <span className="text-sm text-[#71717A]">Sto analizzando...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="p-4 border-t border-[#E4E4E7] bg-white">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Descrivi il problema tecnico..."
              className="flex-1 border-[#E4E4E7]"
              disabled={loading}
              data-testid="ai-chat-input"
            />
            <Button 
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="bg-[#FDE047] text-[#0F172A] hover:bg-[#FACC15] shadow-[0_0_15px_rgba(253,224,71,0.3)]"
              data-testid="send-message-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-[#71717A] mt-2">
            <Sparkles className="w-3 h-3 inline mr-1" />
            Powered by OpenAI GPT-5.2 • L'IA può commettere errori, verifica sempre le informazioni
          </p>
        </div>
      </Card>
    </div>
  );
}
